import { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import Link from '@fuse/core/Link';
import { apiClient } from '@/api/client';
import type { ProblemDetails } from '@/api/data-contracts';

type ValidationProblemDetails = ProblemDetails & {
	errors?: Record<string, string[]> | null;
};

/**
 * Reset-password confirmation page. Reads the userName + token from the URL query string
 * (delivered by the email link), prompts for a new password with the standard policy
 * checklist, and on success redirects to the sign-in page.
 */
function ResetPasswordView() {
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const userName = params.get('userName') ?? '';
	const token = params.get('token') ?? '';

	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showNew, setShowNew] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const [formError, setFormError] = useState<string | null>(null);

	const isLongEnough = newPassword.length >= 8;
	const hasUppercase = /[A-Z]/.test(newPassword);
	const hasLowercase = /[a-z]/.test(newPassword);
	const hasDigit = /[0-9]/.test(newPassword);
	const passwordsMatch = !!newPassword && newPassword === confirmPassword;
	const isPasswordValid = isLongEnough && hasUppercase && hasLowercase && hasDigit;

	const linkLooksOk = !!userName && !!token;

	const mutation = useMutation({
		mutationFn: async () => {
			setFormError(null);
			const res = await apiClient.resetPassword({
				userName,
				token,
				newPassword,
				confirmNewPassword: confirmPassword
			});
			return res.data;
		},
		onSuccess: () => {
			// Hard redirect to sign-in with a hint so the user knows what to do next.
			navigate('/sign-in?passwordReset=1', { replace: true });
		},
		onError: (err: unknown) => {
			const problem = (err as { error?: ValidationProblemDetails | ProblemDetails })?.error;
			const detail = problem?.detail || problem?.title;
			const errs = (problem as ValidationProblemDetails | undefined)?.errors;
			const flat = errs ? Object.values(errs).flat().filter(Boolean) : [];
			setFormError(
				flat.length
					? flat.join(' ')
					: typeof detail === 'string' && detail
						? detail
						: 'The reset link is invalid or has expired. Please request a new one.'
			);
		}
	});

	const disableSubmit = useMemo(
		() => !linkLooksOk || !isPasswordValid || !passwordsMatch || mutation.isPending,
		[linkLooksOk, isPasswordValid, passwordsMatch, mutation.isPending]
	);

	return (
		<div className="flex min-w-0 flex-auto flex-col items-center sm:justify-center">
			<Paper className="min-h-full w-full rounded-none px-4 py-8 sm:min-h-auto sm:w-auto sm:rounded-xl sm:p-12 sm:shadow-sm">
				<Box className="mx-auto flex w-full max-w-80 flex-col gap-6 sm:mx-0 sm:w-80">
					<div className="w-full">
						<img
							className="w-12"
							src="/assets/images/logo/TSELogo.png"
							alt="logo"
						/>
						<Typography className="mt-8 text-4xl leading-[1.25] font-extrabold tracking-tight">
							Reset password
						</Typography>
						{userName && (
							<Typography
								className="mt-2"
								color="text.secondary"
							>
								For account: <b>{userName}</b>
							</Typography>
						)}
					</div>

					{!linkLooksOk && (
						<Alert severity="error">
							This reset link is incomplete. Please request a new one from the sign-in page.
						</Alert>
					)}

					<TextField
						label="New password"
						fullWidth
						type={showNew ? 'text' : 'password'}
						value={newPassword}
						onChange={(e) => setNewPassword(e.target.value)}
						disabled={!linkLooksOk}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										onClick={() => setShowNew((s) => !s)}
										edge="end"
										aria-label="toggle new password visibility"
									>
										{showNew ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							)
						}}
					/>

					<Box>
						{[
							{ label: 'At least 8 characters', met: isLongEnough },
							{ label: 'Contains an uppercase letter', met: hasUppercase },
							{ label: 'Contains a lowercase letter', met: hasLowercase },
							{ label: 'Contains a number', met: hasDigit }
						].map(({ label, met }) => (
							<Box
								key={label}
								display="flex"
								alignItems="center"
								gap={0.75}
								sx={{ py: 0.25 }}
							>
								{met ? (
									<CheckCircleOutlineIcon sx={{ fontSize: 16, color: 'success.main' }} />
								) : (
									<RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
								)}
								<Typography
									variant="caption"
									color={met ? 'success.main' : 'text.secondary'}
								>
									{label}
								</Typography>
							</Box>
						))}
					</Box>

					<TextField
						label="Confirm new password"
						fullWidth
						type={showConfirm ? 'text' : 'password'}
						value={confirmPassword}
						onChange={(e) => setConfirmPassword(e.target.value)}
						disabled={!linkLooksOk}
						error={!!confirmPassword && confirmPassword !== newPassword}
						helperText={confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : ' '}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										onClick={() => setShowConfirm((s) => !s)}
										edge="end"
										aria-label="toggle confirm password visibility"
									>
										{showConfirm ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							)
						}}
					/>

					{formError && <Alert severity="error">{formError}</Alert>}

					<Button
						type="button"
						variant="contained"
						color="secondary"
						size="large"
						disabled={disableSubmit}
						onClick={() => mutation.mutate()}
					>
						{mutation.isPending ? 'Resetting…' : 'Reset password'}
					</Button>

					<Link
						className="text-md self-center font-medium"
						to="/sign-in"
					>
						Back to sign in
					</Link>
				</Box>
			</Paper>
		</div>
	);
}

export default ResetPasswordView;
